/**
 * Usage Tracking Schemas
 *
 * Zod validation schemas for connector usage tracking and rate limiting.
 * Issue #244: Added for per-tenant usage tracking and billing.
 */

import { z } from 'zod';

// ============================================================================
// USAGE TRACKING SCHEMAS
// ============================================================================

/**
 * Connector usage record schema
 * NOTE: Named "Connector" prefix to avoid conflict with billing.dto.ts UsageRecord
 */
export const ConnectorUsageRecordSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().min(1),
  connectorId: z.string().uuid().optional(),
  connectorType: z.string().min(1),
  operation: z.string().min(1),
  count: z.number().int().positive(),
  date: z.string().datetime(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
});
export type ConnectorUsageRecord = z.infer<typeof ConnectorUsageRecordSchema>;

/**
 * Connector usage summary schema
 * NOTE: Named "Connector" prefix to avoid conflict with billing.dto.ts UsageSummary
 */
export const ConnectorUsageSummarySchema = z.object({
  businessId: z.string().min(1),
  connectorType: z.string().min(1),
  period: z.enum(['day', 'week', 'month']),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  totalCalls: z.number().int().nonnegative(),
  totalRecordsProcessed: z.number().int().nonnegative().optional(),
  operationBreakdown: z.record(z.string(), z.number().int().nonnegative()),
  dailyBreakdown: z
    .array(
      z.object({
        date: z.string(),
        count: z.number().int().nonnegative(),
      }),
    )
    .optional(),
});
export type ConnectorUsageSummary = z.infer<typeof ConnectorUsageSummarySchema>;

/**
 * Usage query parameters
 */
export const UsageQuerySchema = z.object({
  businessId: z.string().min(1),
  connectorType: z.string().optional(),
  operation: z.string().optional(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  groupBy: z.enum(['day', 'week', 'month', 'operation', 'connector']).optional(),
});
export type UsageQuery = z.infer<typeof UsageQuerySchema>;

// ============================================================================
// RATE LIMITING SCHEMAS
// ============================================================================

/**
 * Rate limit configuration schema
 */
export const RateLimitConfigSchema = z.object({
  connectorType: z.string().min(1),
  // Per-minute limits
  requestsPerMinute: z.number().int().positive(),
  // Per-day limits
  requestsPerDay: z.number().int().positive(),
  // Burst configuration
  burstLimit: z.number().int().positive().optional(),
  burstWindowSeconds: z.number().int().positive().default(60),
  // Throttling behavior
  throttleOnExceed: z.boolean().default(true),
  rejectOnExceed: z.boolean().default(false),
});
export type RateLimitConfig = z.infer<typeof RateLimitConfigSchema>;

/**
 * Rate limit status schema
 */
export const RateLimitStatusSchema = z.object({
  businessId: z.string().min(1),
  connectorType: z.string().min(1),
  currentMinuteUsage: z.number().int().nonnegative(),
  currentDayUsage: z.number().int().nonnegative(),
  minuteLimit: z.number().int().positive(),
  dayLimit: z.number().int().positive(),
  isThrottled: z.boolean(),
  resetAt: z.string().datetime(),
  dayResetAt: z.string().datetime(),
});
export type RateLimitStatus = z.infer<typeof RateLimitStatusSchema>;

// ============================================================================
// SUBSCRIPTION TIER LIMITS
// ============================================================================

/**
 * Subscription tier usage limits
 */
export const SubscriptionTierLimitsSchema = z.object({
  tier: z.enum(['free', 'starter', 'professional', 'enterprise']),
  connectorLimits: z.record(
    z.string(),
    z.object({
      requestsPerDay: z.number().int().positive(),
      requestsPerMonth: z.number().int().positive(),
      batchSize: z.number().int().positive(),
      features: z.array(z.string()),
    }),
  ),
  globalLimits: z.object({
    totalConnectors: z.number().int().positive(),
    totalRequestsPerDay: z.number().int().positive(),
    totalRequestsPerMonth: z.number().int().positive(),
    dataRetentionDays: z.number().int().positive(),
  }),
});
export type SubscriptionTierLimits = z.infer<typeof SubscriptionTierLimitsSchema>;

/**
 * Default tier limits for SFR Analytics
 */
export const DefaultSfrAnalyticsTierLimits: Record<
  string,
  { requestsPerDay: number; requestsPerMonth: number; batchSize: number }
> = {
  free: { requestsPerDay: 100, requestsPerMonth: 1000, batchSize: 10 },
  starter: { requestsPerDay: 500, requestsPerMonth: 10000, batchSize: 25 },
  professional: { requestsPerDay: 2000, requestsPerMonth: 50000, batchSize: 100 },
  enterprise: { requestsPerDay: 10000, requestsPerMonth: 500000, batchSize: 500 },
};

/**
 * Default tier limits for Retell AI (voice calls)
 * Issue #300: Added for outbound voice call tracking
 */
export const DefaultRetellTierLimits: Record<
  string,
  { requestsPerDay: number; requestsPerMonth: number; callMinutesPerMonth: number }
> = {
  free: { requestsPerDay: 10, requestsPerMonth: 100, callMinutesPerMonth: 60 },
  starter: { requestsPerDay: 50, requestsPerMonth: 500, callMinutesPerMonth: 300 },
  professional: { requestsPerDay: 200, requestsPerMonth: 2000, callMinutesPerMonth: 1500 },
  enterprise: { requestsPerDay: 1000, requestsPerMonth: 20000, callMinutesPerMonth: 10000 },
};

/**
 * Default tier limits for Resend (emails)
 * Issue #300: Added for email tracking
 */
export const DefaultResendTierLimits: Record<
  string,
  { requestsPerDay: number; requestsPerMonth: number; batchSize: number }
> = {
  free: { requestsPerDay: 100, requestsPerMonth: 1000, batchSize: 10 },
  starter: { requestsPerDay: 1000, requestsPerMonth: 10000, batchSize: 50 },
  professional: { requestsPerDay: 5000, requestsPerMonth: 50000, batchSize: 100 },
  enterprise: { requestsPerDay: 50000, requestsPerMonth: 500000, batchSize: 100 },
};

/**
 * Default tier limits for DocuSeal (e-signatures)
 * Issue #300: Added for e-signature tracking
 */
export const DefaultDocuSealTierLimits: Record<
  string,
  { requestsPerDay: number; requestsPerMonth: number; submissionsPerMonth: number }
> = {
  free: { requestsPerDay: 10, requestsPerMonth: 50, submissionsPerMonth: 10 },
  starter: { requestsPerDay: 50, requestsPerMonth: 500, submissionsPerMonth: 50 },
  professional: { requestsPerDay: 200, requestsPerMonth: 2000, submissionsPerMonth: 200 },
  enterprise: { requestsPerDay: 1000, requestsPerMonth: 20000, submissionsPerMonth: 2000 },
};

/**
 * Combined tier limits by connector type
 * Issue #300: Unified access to all connector limits
 */
export const DefaultConnectorTierLimits: Record<
  string,
  Record<string, { requestsPerDay: number; requestsPerMonth: number }>
> = {
  free: {
    'sfr-analytics': { requestsPerDay: 100, requestsPerMonth: 1000 },
    retell: { requestsPerDay: 10, requestsPerMonth: 100 },
    resend: { requestsPerDay: 100, requestsPerMonth: 1000 },
    docuseal: { requestsPerDay: 10, requestsPerMonth: 50 },
    salesforce: { requestsPerDay: 500, requestsPerMonth: 5000 },
    slack: { requestsPerDay: 500, requestsPerMonth: 5000 },
  },
  starter: {
    'sfr-analytics': { requestsPerDay: 500, requestsPerMonth: 10000 },
    retell: { requestsPerDay: 50, requestsPerMonth: 500 },
    resend: { requestsPerDay: 1000, requestsPerMonth: 10000 },
    docuseal: { requestsPerDay: 50, requestsPerMonth: 500 },
    salesforce: { requestsPerDay: 2000, requestsPerMonth: 20000 },
    slack: { requestsPerDay: 2000, requestsPerMonth: 20000 },
  },
  professional: {
    'sfr-analytics': { requestsPerDay: 2000, requestsPerMonth: 50000 },
    retell: { requestsPerDay: 200, requestsPerMonth: 2000 },
    resend: { requestsPerDay: 5000, requestsPerMonth: 50000 },
    docuseal: { requestsPerDay: 200, requestsPerMonth: 2000 },
    salesforce: { requestsPerDay: 10000, requestsPerMonth: 100000 },
    slack: { requestsPerDay: 10000, requestsPerMonth: 100000 },
  },
  enterprise: {
    'sfr-analytics': { requestsPerDay: 10000, requestsPerMonth: 500000 },
    retell: { requestsPerDay: 1000, requestsPerMonth: 20000 },
    resend: { requestsPerDay: 50000, requestsPerMonth: 500000 },
    docuseal: { requestsPerDay: 1000, requestsPerMonth: 20000 },
    salesforce: { requestsPerDay: 100000, requestsPerMonth: 1000000 },
    slack: { requestsPerDay: 100000, requestsPerMonth: 1000000 },
  },
};

// ============================================================================
// USAGE EVENTS (FOR BILLING SERVICE)
// Issue #300: Events emitted to billing service for metered billing
// ============================================================================

/**
 * Usage event types for billing
 */
export const UsageEventTypeSchema = z.enum([
  'API_CALL',
  'VOICE_CALL_MINUTE',
  'EMAIL_SENT',
  'DOCUMENT_SIGNED',
  'DATA_ENRICHMENT',
  'QUOTA_WARNING',
  'QUOTA_EXCEEDED',
]);
export type UsageEventType = z.infer<typeof UsageEventTypeSchema>;

/**
 * Usage event schema for billing
 */
export const UsageEventSchema = z.object({
  eventId: z.string().uuid(),
  eventType: UsageEventTypeSchema,
  businessId: z.string().min(1),
  connectorType: z.string().min(1),
  operation: z.string().min(1),
  quantity: z.number().int().positive(),
  timestamp: z.string().datetime(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  billingPeriod: z.object({
    year: z.number().int().min(2020),
    month: z.number().int().min(1).max(12),
  }),
});
export type UsageEvent = z.infer<typeof UsageEventSchema>;

/**
 * Billable unit rates by connector type (in cents)
 * These are platform defaults; actual rates may vary by subscription
 */
export const DefaultBillableUnitRates: Record<string, { unitType: string; rateCents: number }> = {
  'sfr-analytics': { unitType: 'API_CALL', rateCents: 1 }, // $0.01 per call
  retell: { unitType: 'VOICE_CALL_MINUTE', rateCents: 10 }, // $0.10 per minute
  resend: { unitType: 'EMAIL_SENT', rateCents: 0.1 }, // $0.001 per email
  docuseal: { unitType: 'DOCUMENT_SIGNED', rateCents: 50 }, // $0.50 per signature
  salesforce: { unitType: 'API_CALL', rateCents: 0 }, // Included in subscription
  slack: { unitType: 'API_CALL', rateCents: 0 }, // Included in subscription
};
